import { useMemo, useState } from "react"
import { Input } from "@/components/ui/input"
import { formatCentral } from "../../lib/program"
import type { PlanEntries, PlanEntry } from "../../types/outreach-program.types"
import { NotAvailable } from "./ProgramPanels"

const ACTION_LABELS: Record<string, string> = {
  book_consent_call: "Book consent call",
  book_checkin_call: "Book check-in call",
  complete: "Complete",
  none: "Nothing",
}

const isBooking = (e: PlanEntry) => e.action === "book_consent_call" || e.action === "book_checkin_call"

/**
 * What the tick decided per patient for one Central day: GET program/plan.
 * The rows are the latest tick's decisions; "Intended bookings" narrows them
 * to the calls it booked (live) or would book (shadow / paused).
 */
export function PlanList({
  plan,
  date,
  onDateChange,
}: {
  plan: PlanEntries | null
  date: string
  onDateChange: (d: string) => void
}) {
  const [reason, setReason] = useState("")
  const [bookingsOnly, setBookingsOnly] = useState(false)
  const reasons = useMemo(() => {
    const counts = new Map<string, number>()
    for (const e of plan?.entries ?? []) counts.set(e.reason, (counts.get(e.reason) ?? 0) + 1)
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [plan])
  const bookings = (plan?.entries ?? []).filter(isBooking)
  const rows = (plan?.entries ?? []).filter((e) => (!reason || e.reason === reason) && (!bookingsOnly || isBooking(e)))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          aria-label="Plan date"
          className="w-40"
          value={date}
          onChange={(e) => e.target.value && onDateChange(e.target.value)}
        />
        <select
          aria-label="Filter by reason"
          className="h-10 rounded-md border border-input bg-card px-2 text-sm"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">All reasons ({plan?.entries?.length ?? 0})</option>
          {reasons.map(([r, n]) => (
            <option key={r} value={r}>
              {r} ({n})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={bookingsOnly}
            onChange={(e) => setBookingsOnly(e.target.checked)}
          />
          Intended bookings only ({bookings.length})
        </label>
      </div>
      {!plan ? (
        <NotAvailable what="Plan" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No plan entries for {plan.plan_date}.</p>
      ) : (
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-xs" data-testid="plan-list">
            <thead className="sticky top-0 bg-card">
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3 font-normal">Patient</th>
                <th className="py-1 pr-3 font-normal">Action</th>
                <th className="py-1 pr-3 font-normal">Slot (Central)</th>
                <th className="py-1 pr-3 text-right font-normal">Attempt</th>
                <th className="py-1 pr-3 font-normal">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={`${e.patient_id}-${e.plan_date}`} className="border-t">
                  <td className="py-1 pr-3">{e.patient_name || e.patient_id}</td>
                  <td className="py-1 pr-3">{ACTION_LABELS[e.action] ?? e.action}</td>
                  <td className="py-1 pr-3">{e.slot_at ? formatCentral(e.slot_at) : "-"}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{e.attempt ?? "-"}</td>
                  <td className="py-1 pr-3 text-muted-foreground">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
